import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { resolveImageUrl } from '@/components/ImageUploader';
import { useSEO } from '@/lib/useSEO';
import { formatCOP, formatDate } from '@/lib/format';
import { Users, Store, Package, ShoppingBag, Wallet, BarChart3, CreditCard } from 'lucide-react';
import { ADMIN } from '@/constants/testIds';
import { AdminPlanExceptionsCard } from '@/components/AdminPlanExceptionsCard';

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [launch, setLaunch] = useState(null);

  useSEO({
    title: 'Dashboard de administración | AutoMarket Colombia',
    description: 'KPIs, configuración de comisiones y top productos de la plataforma.',
    canonical: '/admin',
  });

  useEffect(() => {
    api.get('/admin/dashboard').then(({ data }) => setData(data));
    api.get('/admin/launch-benefit/status').then(({ data }) => setLaunch(data)).catch(() => setLaunch(null));
  }, []);

  if (!data) return <Layout><div className="p-12 text-center text-slate-500">Cargando...</div></Layout>;
  const { kpis, settings, top_products } = data;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Dashboard general</h1>
          </div>
          <nav className="flex gap-2 flex-wrap">
            <Link to="/admin/usuarios" className="px-3 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-100">Usuarios</Link>
            <Link to="/admin/vendedores" className="px-3 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-100">Vendedores</Link>
            <Link to="/admin/pedidos" className="px-3 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-100">Pedidos</Link>
            <Link to="/admin/novedades" data-testid="admin-nav-novedades" className="px-3 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-100">Novedades</Link>
            <Link to="/admin/pqr" data-testid="admin-nav-pqr" className="px-3 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-100">PQR</Link>
            <Link to="/admin/payouts" data-testid="admin-nav-payouts" className="px-3 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-100">Payouts</Link>
            <Link to="/admin/documentos-legales" data-testid="admin-nav-legal-docs" className="px-3 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-100">Documentos legales</Link>
            <Link to="/admin/configuracion" className="px-3 py-2 text-sm font-semibold border border-slate-300 hover:bg-slate-100">Configuración</Link>
          </nav>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi testid={ADMIN.kpiBuyers} icon={Users} label="Compradores" value={kpis.buyers} />
          <Kpi testid={ADMIN.kpiSellers} icon={Store} label="Vendedores" value={kpis.sellers} />
          <Kpi testid={ADMIN.kpiProducts} icon={Package} label="Productos" value={kpis.products} />
          <Kpi testid={ADMIN.kpiOrders} icon={ShoppingBag} label="Pedidos" value={kpis.orders_total} sub={`${kpis.orders_delivered} entregados`} />
          <Kpi testid={ADMIN.kpiSales} icon={Wallet} label="Ventas totales" value={formatCOP(kpis.sales_total)} accent />
          <Kpi testid={ADMIN.kpiCommission} icon={BarChart3} label="Comisión generada" value={formatCOP(kpis.commission_total)} accent />
          <Kpi testid={ADMIN.kpiSubscriptionIncome} icon={CreditCard} label="Ingresos suscripción" value={formatCOP(kpis.subscription_income)} accent />
          <Kpi icon={ShoppingBag} label="Pedidos pendientes" value={kpis.orders_pending} />
        </div>

        {launch && (
          <div
            data-testid="admin-launch-benefit-card"
            className="mt-6 border-2 border-orange-500 bg-orange-50 p-5 flex flex-col md:flex-row md:items-center gap-4"
          >
            <div className="flex-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-orange-700">Beneficio de lanzamiento</div>
              <div className="mt-1 font-display text-2xl font-black text-slate-900" data-testid="admin-launch-benefit-label">
                {launch.label}
              </div>
              <div className="mt-1 text-xs text-slate-600">
                Modalidad sin costo y comisión del {launch.commission_pct}% durante {launch.days} días para los primeros {launch.limit} vendedores aprobados.
                Restantes: <span className="font-bold text-slate-900" data-testid="admin-launch-benefit-remaining">{launch.remaining}</span>
                {launch.exhausted && <span className="ml-2 font-bold text-orange-700">· AGOTADO</span>}
              </div>
              <div className="mt-2 h-2 bg-orange-200 overflow-hidden">
                <div
                  className="h-full bg-orange-600 transition-all"
                  style={{ width: `${Math.min(100, (launch.used / launch.limit) * 100)}%` }}
                />
              </div>
            </div>
            {launch.beneficiaries?.length > 0 && (
              <details className="md:w-96 text-xs">
                <summary className="cursor-pointer font-semibold text-orange-700 select-none" data-testid="admin-launch-benefit-toggle">
                  Ver beneficiarios ({launch.beneficiaries.length})
                </summary>
                <div className="mt-2 max-h-40 overflow-y-auto border border-orange-200 bg-white divide-y divide-orange-100">
                  {launch.beneficiaries.map((b) => (
                    <div key={b.user_id} className="px-3 py-1.5 flex items-center gap-2" data-testid={`admin-launch-beneficiary-${b.launch_benefit_slot}`}>
                      <span className="font-mono font-bold text-orange-700 w-8">#{b.launch_benefit_slot}</span>
                      <span className="flex-1 truncate text-slate-800">{b.business_name || b.user_id}</span>
                      <span className={b.active ? 'text-emerald-700 font-semibold' : 'text-slate-500'} data-testid={`admin-launch-beneficiary-expires-${b.launch_benefit_slot}`}>
                        {b.active ? 'vence' : 'venció'} {formatDate(b.expires_at)}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        <AdminPlanExceptionsCard />

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-slate-200 bg-white p-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Configuración actual</div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
              <div className="border-l-2 border-orange-500 pl-3">
                <div className="text-orange-700 text-[10px] uppercase tracking-wider font-bold">Precio Sin plan</div>
                <div className="font-display text-xl font-black text-slate-900 leading-tight">{formatCOP(settings.none_plan_price)}</div>
                <div className="text-[10px] text-slate-500">cada {settings.none_plan_period_months} mes(es)</div>
              </div>
              <div className="border-l-2 border-slate-300 pl-3">
                <div className="text-slate-600 text-[10px] uppercase tracking-wider font-bold">Sin plan</div>
                <div className="font-display text-xl font-black text-slate-900 leading-tight">{settings.commission_pct}%</div>
                <div className="text-[10px] text-slate-500">comisión default</div>
              </div>
              <div className="border-l-2 border-slate-900 pl-3">
                <div className="text-slate-700 text-[10px] uppercase tracking-wider font-bold">Básico</div>
                <div className="font-display text-xl font-black text-slate-900 leading-tight">{settings.basic_plan_commission_pct}%</div>
                <div className="text-[10px] text-slate-500">{formatCOP(settings.basic_plan_price)}</div>
              </div>
              <div className="border-l-2 border-orange-400 pl-3 col-span-3">
                <div className="text-orange-700 text-[10px] uppercase tracking-wider font-bold">PRO</div>
                <div className="font-display text-xl font-black text-slate-900 leading-tight">
                  {settings.pro_plan_commission_pct}% · {formatCOP(settings.pro_plan_price)}
                </div>
              </div>
            </div>
            <Link to="/admin/configuracion" className="inline-block mt-4 text-sm font-semibold text-blue-700 hover:underline">
              Editar configuración →
            </Link>
          </div>

          <div className="border border-slate-200 bg-white p-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Top 5 productos por ingreso</div>
            <div className="mt-3 divide-y divide-slate-100">
              {top_products.length === 0 && <div className="py-4 text-sm text-slate-500">Sin ventas aún</div>}
              {top_products.map((p, i) => (
                <div key={p._id} className="py-2 flex items-center gap-3">
                  <div className="font-display text-2xl font-black text-slate-400 w-6">{i + 1}</div>
                  {p.image && <img src={resolveImageUrl(p.image)} alt="" className="h-10 w-10 object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.qty} vendidos</div>
                  </div>
                  <div className="font-display font-bold text-slate-900">{formatCOP(p.revenue)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const Kpi = ({ icon: Icon, label, value, sub, testid, accent }) => (
  <div data-testid={testid} className={`border p-4 ${accent ? 'border-orange-500 bg-orange-50' : 'border-slate-200 bg-white'}`}>
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-slate-500" />
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    </div>
    <div className="mt-2 font-display text-2xl font-black text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);
